import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import HrefLangTags from "../components/HrefLangTags";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { useLanguage } from "../contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WorksDetailPage() {
  const { lang, slug } = useParams();
  const { t } = useLanguage();
  const [work, setWork] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    axios.get(`${API}/works/${slug}`, { params: { lang } })
      .then((res) => { if (!cancelled) { setWork(res.data); setStatus("ok"); } })
      .catch(() => { if (!cancelled) setStatus("not-found"); });
    return () => { cancelled = true; };
  }, [lang, slug]);

  if (status === "not-found") {
    return <Navigate to={`/${lang}/works`} replace />;
  }

  if (!work) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <div className="container-page py-32">
          <div className="h-12 w-1/2 rounded-2xl bg-mist animate-pulse mb-6" />
          <div className="h-[420px] rounded-3xl bg-mist animate-pulse" />
        </div>
      </div>
    );
  }

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": work.title,
    "creator": { "@type": "Organization", "name": "Yastık Medya" },
    "about": work.client,
    "datePublished": String(work.year),
    "image": work.cover,
    "description": work.excerpt,
  };

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="works-detail-page" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Helmet>
        <title>{`${work.title} — Yastık Medya`}</title>
        <meta name="description" content={work.excerpt} />
        <meta property="og:title" content={`${work.title} · ${work.client}`} />
        <meta property="og:description" content={work.excerpt} />
        <meta property="og:image" content={work.cover} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(ldJson)}</script>
      </Helmet>
      <HrefLangTags pathSuffix={`works/${slug}`} />
      <Header />

      {/* Floating prev/next navigation arrows */}
      {work.prev_slug && (
        <Link
          to={`/${lang}/works/${work.prev_slug}`}
          data-testid="work-prev-float"
          aria-label="Önceki proje"
          className="fixed left-4 top-1/2 -translate-y-1/2 z-30 hidden md:flex
                     h-12 w-12 items-center justify-center rounded-full
                     bg-cream/90 backdrop-blur-md border border-mist shadow-lg
                     text-ink hover:border-brand-pink hover:bg-brand-pink hover:text-cream
                     transition-colors duration-200"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
      )}
      {work.next_slug && (
        <Link
          to={`/${lang}/works/${work.next_slug}`}
          data-testid="work-next-float"
          aria-label="Sonraki proje"
          className="fixed right-4 top-1/2 -translate-y-1/2 z-30 hidden md:flex
                     h-12 w-12 items-center justify-center rounded-full
                     bg-cream/90 backdrop-blur-md border border-mist shadow-lg
                     text-ink hover:border-brand-pink hover:bg-brand-pink hover:text-cream
                     transition-colors duration-200"
        >
          <ArrowRight className="h-5 w-5" />
        </Link>
      )}

      <main>
        {/* Hero with cover */}
        <section className="relative">
          <div className="container-page pt-8 pb-6">
            <Link
              to={`/${lang}/works`}
              data-testid="works-back-link"
              className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {lang === "tr" ? "Tüm projeler" : "All projects"}
            </Link>
          </div>
          <div className="container-page">
            <div className="grid gap-8 lg:grid-cols-12 lg:gap-12 items-end">
              <Reveal as="div" delay={0.05} className="lg:col-span-7">
                <span className="eyebrow">
                  <span className="h-1 w-1 rounded-full bg-brand-pink" />
                  {work.category}
                </span>
                <h1
                  data-testid="work-title"
                  className="mt-5 font-display font-extrabold leading-[0.95]"
                  style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }}
                >
                  {work.title}
                </h1>
                <p className="mt-6 text-body-lg text-ink/65 leading-relaxed max-w-xl">{work.excerpt}</p>
              </Reveal>

              <Reveal as="dl" delay={0.12} className="lg:col-span-5 grid grid-cols-2 gap-x-6 gap-y-5 text-sm">
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-ink/45">
                    {lang === "tr" ? "Müşteri" : "Client"}
                  </dt>
                  <dd className="mt-1.5 font-semibold text-ink leading-snug">{work.client}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-ink/45">
                    {lang === "tr" ? "Yıl" : "Year"}
                  </dt>
                  <dd className="mt-1.5 font-semibold text-ink">{work.year}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-[10px] uppercase tracking-[0.22em] text-ink/45">
                    {lang === "tr" ? "Hizmetler" : "Services"}
                  </dt>
                  <dd className="mt-1.5 flex flex-wrap gap-1.5">
                    {(work.services || []).map((s) => (
                      <span key={s} className="rounded-full bg-mist text-ink/80 px-2.5 py-1 text-xs">{s}</span>
                    ))}
                  </dd>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Cover image — full bleed inside container */}
          <Reveal as="div" delay={0.18} className="container-page mt-10 lg:mt-14">
            <div className="relative overflow-hidden rounded-3xl border border-mist aspect-[16/9]">
              <img src={work.cover} alt={work.title} className="absolute inset-0 h-full w-full object-cover" />
            </div>
            {!work.real_content && (
              <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-ink/45">
                {lang === "tr"
                  ? "★ Vaka çalışması içeriği AI tabanlı temsil amaçlıdır. Kreatif proje ve müşteri gerçektir."
                  : "★ Case study text is an AI-generated representation. Creative project and client are real."}
              </p>
            )}
          </Reveal>
        </section>

        {/* Metrics */}
        {work.metrics?.length > 0 && (
          <section className="container-page py-section">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {work.metrics.map((m, i) => (
                <Reveal as="div" key={i} delay={i * 0.05}
                  className="rounded-2xl bg-white border border-mist p-6 sm:p-7">
                  <div className="font-display font-extrabold leading-none gold-foil-text"
                       style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)" }}>
                    {m.value}
                  </div>
                  <div className="mt-3 text-sm text-ink/65 leading-snug">{m.label}</div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* Story sections */}
        <section className="container-page pb-section">
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal as="article" className="lg:col-span-8 lg:col-start-3 space-y-14 prose-yastik">
              {work.challenge && (
                <div>
                  <span className="eyebrow"><span className="h-1 w-1 rounded-full bg-brand-pink" />
                    {lang === "tr" ? "Zorluk" : "Challenge"}
                  </span>
                  <div className="mt-4 prose-content">
                    <ReactMarkdown>{work.challenge}</ReactMarkdown>
                  </div>
                </div>
              )}
              {work.approach && (
                <div>
                  <span className="eyebrow"><span className="h-1 w-1 rounded-full bg-gold" />
                    {lang === "tr" ? "Yaklaşım" : "Approach"}
                  </span>
                  <div className="mt-4 prose-content">
                    <ReactMarkdown>{work.approach}</ReactMarkdown>
                  </div>
                </div>
              )}
              {work.results && (
                <div>
                  <span className="eyebrow"><span className="h-1 w-1 rounded-full bg-postun-blue" />
                    {lang === "tr" ? "Sonuçlar" : "Results"}
                  </span>
                  <div className="mt-4 prose-content">
                    <ReactMarkdown>{work.results}</ReactMarkdown>
                  </div>
                </div>
              )}
            </Reveal>
          </div>
        </section>

        {/* Gallery */}
        {(work.gallery?.length > 0) && (
          <section className="container-page pb-section">
            <div className="grid gap-4 sm:grid-cols-2">
              {work.gallery.map((src, i) => (
                <Reveal as="div" key={i} delay={i * 0.06}
                  className="aspect-[4/3] overflow-hidden rounded-3xl border border-mist bg-mist">
                  <img src={src} alt={`${work.title} ${i+1}`} className="h-full w-full object-cover" />
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* Prev/Next + Contact CTA */}
        <section className="container-page pb-section">
          <div className="grid gap-4 sm:grid-cols-2">
            {work.prev_slug ? (
              <Link to={`/${lang}/works/${work.prev_slug}`} data-testid="work-prev"
                className="rounded-3xl border border-mist bg-white p-6 sm:p-8 group hover:border-brand-pink/60 transition-colors">
                <span className="text-xs uppercase tracking-[0.18em] text-ink/45 inline-flex items-center gap-1">
                  <ArrowLeft className="h-3 w-3" />
                  {lang === "tr" ? "Önceki proje" : "Previous"}
                </span>
                <div className="mt-3 font-display text-h2 font-semibold text-ink group-hover:text-brand-pink-deep transition-colors">
                  {work.prev_slug.replace(/-/g," ")}
                </div>
              </Link>
            ) : <div />}
            {work.next_slug ? (
              <Link to={`/${lang}/works/${work.next_slug}`} data-testid="work-next"
                className="rounded-3xl border border-mist bg-white p-6 sm:p-8 group hover:border-brand-pink/60 transition-colors text-end">
                <span className="text-xs uppercase tracking-[0.18em] text-ink/45 inline-flex items-center gap-1">
                  {lang === "tr" ? "Sonraki proje" : "Next"}
                  <ArrowRight className="h-3 w-3" />
                </span>
                <div className="mt-3 font-display text-h2 font-semibold text-ink group-hover:text-brand-pink-deep transition-colors">
                  {work.next_slug.replace(/-/g," ")}
                </div>
              </Link>
            ) : null}
          </div>

          <Reveal as="div" delay={0.08} className="mt-10 rounded-3xl bg-ink text-cream p-10 sm:p-14 relative overflow-hidden">
            <div className="absolute -top-24 -end-24 h-[360px] w-[360px] rounded-full bg-brand-pink/25 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
              <div>
                <span className="eyebrow text-cream/60">
                  <span className="h-1 w-1 rounded-full bg-brand-pink" />
                  {lang === "tr" ? "Benzer bir proje mi düşünüyorsun?" : "Got a similar project in mind?"}
                </span>
                <h3 className="mt-4 font-display text-display-2 font-semibold">
                  {lang === "tr" ? <>Bir <span className="gold-foil-text">brief</span> kadar yakın.</>
                                : <>One <span className="gold-foil-text">brief</span> away.</>}
                </h3>
              </div>
              <Link to={`/${lang}/contact`} className="btn-gold flex-shrink-0" data-testid="work-cta-contact">
                {lang === "tr" ? "İletişim" : "Get in touch"}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
    </div>
  );
}
